/// <amd-module name="Wit/WorkItemModel/WorkItem/FieldUtils" />
import { IObservableArray, ObservableArray } from "VSS/Core/Observable";
import { dateToString, format, localeComparer, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { isIsoDate } from "Wit/Common/DateUtils";
import { CoreField, FieldFlags, FieldType } from "Wit/Common/Generated/Constants";
import {
    convertPotentialIdentityRefFromFieldValueNotAsIdentityRef,
    parseUniquefiedIdentityName
} from "Wit/Identity/Utilities/WorkItemIdentityHelper";
import { FieldStatus } from "Wit/WorkItemModel/OM";
import {
    FieldStatusFlags,
    IField,
    IFieldUpdate,
    IItemGlobalValueList,
    IValueStatus,
    IWorkItem,
    IWorkItemStore
} from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import * as Resources from "Wit/WorkItemModel/Resources";
import { ServerDefaultValue } from "Wit/WorkItemModel/RuleEngine/ServerDefaultValue"; // TODO: should the field model depend on the Rule Engine? Or vice-versa?
import { containsControlChars, containsMismatchedSurrogateChars, parseBool, parseNumber } from "Wit/WorkItemModel/Utils";
import { copySortFlag, flagSorted, sortIfNotSorted } from "Wit/WorkItemModel/WorkItem/Sorting";
import { FieldValidationKey } from "../RuleEngine/FieldValidationKey";
import * as FieldDefinitionUtils from "./FieldDefinitionUtils";

const fieldStatusMap = [
    FieldStatusFlags.None,
    FieldStatusFlags.InvalidEmpty,
    FieldStatusFlags.InvalidNotEmpty,
    FieldStatusFlags.InvalidFormat,
    FieldStatusFlags.InvalidListValue,
    FieldStatusFlags.InvalidOldValue,
    FieldStatusFlags.InvalidNotOldValue,
    FieldStatusFlags.InvalidEmptyOrOldValue,
    FieldStatusFlags.InvalidNotEmptyOrOldValue,
    FieldStatusFlags.InvalidValueInOtherField,
    FieldStatusFlags.InvalidValueNotInOtherField,
    FieldStatusFlags.InvalidDate,
    FieldStatusFlags.InvalidTooLong,
    FieldStatusFlags.InvalidType,
    FieldStatusFlags.InvalidComputedField,
    FieldStatusFlags.InvalidPath,
    FieldStatusFlags.InvalidCharacters,
    FieldStatusFlags.InvalidIdentityField
];

const SqlIntMaxValue = 2147483648;
const MAX_TITLE_LENGTH = 256;
const guidRegex = /^\{?([\dA-F]{8})-?([\dA-F]{4})-?([\dA-F]{4})-?([\dA-F]{4})-?([\dA-F]{12})\}?$/i;

export function resetField(field: IField) {
    field.status = FieldStatusFlags.None;
    field.allowedValues = undefined;
    field.lists = undefined;
}

export function compareFieldValues(
    value1: unknown,
    value2: unknown,
    caseInsensitive?: boolean,
    identityComparisonNeeded?: boolean
): boolean {
    if (value1 instanceof ServerDefaultValue) {
        if (value2 instanceof ServerDefaultValue && value1.type === value2.type) {
            return compareFieldValues(value1.value, value2.value, caseInsensitive);
        } else {
            return false;
        }
    } else if (value2 instanceof ServerDefaultValue) {
        return false;
    }

    value1 = convertPotentialIdentityRefFromFieldValueNotAsIdentityRef(value1);
    value2 = convertPotentialIdentityRefFromFieldValueNotAsIdentityRef(value2);

    if (value1 === null || typeof value1 === "undefined") {
        value1 = "";
    }

    if (value2 === null || typeof value2 === "undefined") {
        value2 = "";
    }

    const value1String = "" + value1;
    const value2String = "" + value2;

    let match = false;
    if (caseInsensitive) {
        match = localeIgnoreCaseComparer(value1String, value2String) === 0;
    } else {
        match = localeComparer(value1String, value2String) === 0;
    }

    if (!match && identityComparisonNeeded) {
        /// This means if the identity strings don't match exactly we will investigate the combo string unique components and compare them
        /// We only need to do this when comparing field value to a stored identity value in the rule (like a when or copy rule)

        const idValue1 = parseUniquefiedIdentityName(value1String);
        const idValue2 = parseUniquefiedIdentityName(value2String);

        // Only if we have a unique name are these both identity values. If either is a non-identity value
        // then let the existing match result stand.
        if (idValue1 && idValue2 && idValue1.uniqueName && idValue2.uniqueName) {
            match = localeIgnoreCaseComparer(idValue1.uniqueName, idValue2.uniqueName) === 0;
        }
    }

    return match;
}

export function convertFieldValueToExternal(value: any, fieldType: FieldType): any {
    if (value === undefined) {
        value = null;
    }

    if (value === null && isStringField(fieldType)) {
        value = "";
    } else if (fieldType === FieldType.Boolean) {
        value = value ? true : false;
    }

    return value;
}

export function convertFieldValueToInternal(value: any, fieldType: FieldType, parseOnlyIsoDates?: boolean): IValueStatus {
    let status = 0;
    let convertedValue: any;
    let year: number;
    let match: RegExpExecArray | null;

    if (value === null || typeof value === "undefined") {
        convertedValue = null;
    } else if (typeof value === "string" && value.length === 0) {
        convertedValue = null;
    } else if (value instanceof ServerDefaultValue) {
        convertedValue = value;
    } else {
        try {
            switch (fieldType) {
                case FieldType.Boolean:
                    if (typeof value === "boolean") {
                        convertedValue = value ? 1 : 0;
                    } else if (typeof value === "number") {
                        convertedValue = value !== 0.0 ? 1 : 0;
                    } else if (typeof value === "string") {
                        if (/^\d+$/.test(value)) {
                            convertedValue = parseNumber(value) !== 0.0 ? 1 : 0;
                        } else {
                            convertedValue = parseBool(value) ? 1 : 0;
                        }
                    } else {
                        throw new Error("Invalid Boolean field value.");
                    }
                    break;
                case FieldType.Integer:
                    if (typeof value === "number") {
                        convertedValue = Math.round(value);
                    } else if (typeof value === "boolean") {
                        convertedValue = value ? 0 : 1;
                    } else if (typeof value === "string") {
                        if (/^[\-+]?\d+$/.test(value.trim())) {
                            convertedValue = Number.parseInt(value);
                        }
                    }

                    // Checking if a number is valid and finite number and less 2^31 -1 and greater than -2^31 which is sql max limit for int
                    if (
                        typeof convertedValue === "undefined" ||
                        isNaN(convertedValue) ||
                        !isFinite(convertedValue) ||
                        convertedValue > SqlIntMaxValue - 1 ||
                        convertedValue < -SqlIntMaxValue
                    ) {
                        throw new Error(`Invalid Integer field value,  ${value}`);
                    }

                    break;

                case FieldType.Double:
                    if (typeof value === "number") {
                        convertedValue = value;
                    } else if (typeof value === "boolean") {
                        convertedValue = value ? 0.0 : 1.0;
                    } else if (typeof value === "string") {
                        convertedValue = parseNumber(value.replace(",", "."));
                    }

                    if (typeof convertedValue === "undefined" || isNaN(convertedValue) || !isFinite(convertedValue)) {
                        throw new Error(`Invalid Double field value,  ${value}`);
                    }

                    break;
                case FieldType.DateTime:
                    if (value instanceof Date) {
                        convertedValue = value;
                    } else if (typeof value === "string" && (isIsoDate(value) || !parseOnlyIsoDates)) {
                        convertedValue = new Date(value);
                    } else {
                        convertedValue = null;
                    }

                    if (convertedValue === null || isNaN(convertedValue)) {
                        throw new Error("Invalid DateTime field value.");
                    }

                    year = convertedValue.getFullYear();

                    if (year < 1753 || year > 9999) {
                        status = FieldStatusFlags.InvalidDate;
                    }

                    break;
                case FieldType.Guid:
                    if (typeof value === "string") {
                        match = guidRegex.exec(value);
                        if (!match) {
                            throw new Error("Invalid GUID field value.");
                        }

                        convertedValue = match[1] + "-" + match[2] + "-" + match[3] + "-" + match[4] + "-" + match[5];
                    } else {
                        throw new Error("Invalid GUID field value.");
                    }
                    break;
                default:
                    if (typeof value === "string") {
                        convertedValue = value;
                    } else if (typeof value === "number") {
                        convertedValue = value.toLocaleString();
                    } else if (typeof value === "boolean") {
                        convertedValue = value ? "True" : "False";
                    } else if (value instanceof Date) {
                        convertedValue = dateToString(value, true);
                    } else if (fieldType === FieldType.History && value["text"]) {
                        convertedValue = value["text"];
                    } else {
                        throw new Error("Invalid String field type.");
                    }

                    if (typeof convertedValue === "undefined") {
                        convertedValue = null;
                    }

                    if (convertedValue !== null) {
                        if (fieldType === FieldType.PlainText || fieldType === FieldType.Html) {
                            convertedValue = (convertedValue as string).trim();
                        }

                        if (convertedValue.length === 0) {
                            convertedValue = null;
                        }
                    }

                    if (convertedValue !== null) {
                        if (fieldType === FieldType.String) {
                            if (convertedValue.length > MAX_TITLE_LENGTH) {
                                status = FieldStatusFlags.InvalidTooLong;
                            }
                        }

                        if (!isValidStringFieldValue(convertedValue, isLongTextField(fieldType))) {
                            status = FieldStatusFlags.InvalidCharacters;
                        }
                    }
                    break;
            }
        } catch (e) {
            return {
                value: value,
                error: e,
                status: FieldStatusFlags.InvalidType
            };
        }
    }

    return {
        value: convertedValue,
        status: status
    };
}

export function tryNormalizeFieldValue(value: string, field: IField): string {
    if (value && typeof value === "string" && fieldHasList(field)) {
        const list = getAllowedFieldValues(field, field.workItem).value;
        value = normalizeFieldValue(list, value);
    }

    return value;
}

export function isValidStringFieldValue(value: string, isLongText: boolean): boolean {
    // Check for control chars (except \t\n\r), if the value is not long text
    if (!isLongText && containsControlChars(value)) {
        return false;
    }

    // Check for high and low surrogate chars.
    return !containsMismatchedSurrogateChars(value);
}

export function isStringField(fieldType: FieldType): boolean {
    switch (fieldType) {
        case FieldType.PlainText:
        case FieldType.Html:
        case FieldType.History:
        case FieldType.String:
            return true;
        default:
            return false;
    }
}

export function normalizeFieldValue(array: string[], value: string): string {
    if (value) {
        const hashtable = fieldHashset(array, true);

        return hashtable[value.toLocaleUpperCase()] || value;
    }

    return value;
}

interface IHashSetInternals {
    __hashSet: { [key: string]: string } | undefined;
    __hashSetCaseInsensitive: { [key: string]: string } | undefined;
}

// TODO:Replat -- can we use built-in javascript hashset nowadays?
export function fieldHashset(array: string[], caseInsensitive?: boolean): { [key: string]: string } {
    const arrayInternal = array as string[] & IHashSetInternals;
    const result: { [key: string]: string } = {};

    if (caseInsensitive) {
        if (arrayInternal.__hashSetCaseInsensitive) {
            return arrayInternal.__hashSetCaseInsensitive;
        }

        for (const value of array) {
            result[value.toLocaleUpperCase()] = value;
        }

        arrayInternal.__hashSetCaseInsensitive = result;
    } else {
        if (arrayInternal.__hashSet) {
            return arrayInternal.__hashSet;
        }

        for (const value of array) {
            result[value] = value;
        }

        arrayInternal.__hashSet = result;
    }

    return result;
}

export function getFieldErrorText(field: IField, value?: unknown): string {
    const status = getFieldStatus(field.status);
    const error = field.error;

    switch (status) {
        case FieldStatus.InvalidEmpty:
            return format(Resources.FieldStatusInvalidEmptyError, field.fieldDefinition.name);
        case FieldStatus.InvalidNotEmpty:
            return format(Resources.FieldStatusInvalidNotEmptyError, field.fieldDefinition.name);
        case FieldStatus.InvalidFormat:
            return format(Resources.FieldStatusInvalidFormatError, field.fieldDefinition.name);
        case FieldStatus.InvalidListValue:
            return format(Resources.FieldStatusInvalidListValueError, field.fieldDefinition.name, value);
        case FieldStatus.InvalidOldValue:
            return format(Resources.FieldStatusInvalidOldValueError, field.fieldDefinition.name);
        case FieldStatus.InvalidNotOldValue:
            return format(Resources.FieldStatusInvalidNotOldValueError, field.fieldDefinition.name);
        case FieldStatus.InvalidEmptyOrOldValue:
            return format(Resources.FieldStatusInvalidEmptyOrOldValueError, field.fieldDefinition.name);
        case FieldStatus.InvalidNotEmptyOrOldValue:
            return format(Resources.FieldStatusInvalidNotEmptyOrOldValueError, field.fieldDefinition.name);
        case FieldStatus.InvalidValueInOtherField:
            return format(Resources.FieldStatusInvalidValueInOtherFieldError, field.fieldDefinition.name);
        case FieldStatus.InvalidValueNotInOtherField:
            return format(Resources.FieldStatusInvalidValueNotInOtherFieldError, field.fieldDefinition.name);
        case FieldStatus.InvalidDate:
            return format(Resources.FieldStatusInvalidDateErrorError, field.fieldDefinition.name);
        case FieldStatus.InvalidTooLong:
            return format(Resources.FieldStatusInvalidTooLongError, field.fieldDefinition.name);
        case FieldStatus.InvalidType:
            return format(Resources.FieldStatusInvalidTypeError, field.fieldDefinition.name);
        case FieldStatus.InvalidComputedField:
            return format(Resources.FieldStatusInvalidComputedFieldError, field.fieldDefinition.name);
        case FieldStatus.InvalidPath:
            return format(Resources.FieldStatusInvalidPathError, field.fieldDefinition.name);
        case FieldStatus.InvalidCharacters:
            return format(Resources.FieldStatusInvalidCharactersError, field.fieldDefinition.name);
        case FieldStatus.InvalidIdentity:
            return error || format(Resources.FieldStatusInvalidListValueError, field.fieldDefinition.name, value);
        default:
            return format(Resources.FieldStatusInvalidUnknownError, field.fieldDefinition.name);
    }
}

export function getFieldStatus(statusFlag: FieldStatusFlags): FieldStatus {
    if ((statusFlag & FieldStatusFlags.InvalidMask) !== FieldStatusFlags.None) {
        for (let i = 1, l = fieldStatusMap.length; i < l; i++) {
            if ((statusFlag & fieldStatusMap[i]) !== FieldStatusFlags.None) {
                return i;
            }
        }

        return FieldStatus.InvalidUnknown;
    } else {
        return FieldStatus.Valid;
    }
}

export function isLongTextField(fieldType: FieldType): boolean {
    switch (fieldType) {
        case FieldType.PlainText:
        case FieldType.Html:
        case FieldType.History:
            return true;
        default:
            return false;
    }
}

export function isUserChange(field: IField, update: IFieldUpdate): boolean {
    if (update) {
        if (update.setByRule) {
            if ((field.status & FieldStatusFlags.SetByComputedRule) !== FieldStatusFlags.None) {
                return true;
            } else {
                return false;
            }
        }

        return true;
    }

    return false;
}

export function fieldHasList(field: IField): boolean {
    return (field.status & FieldStatusFlags.HasValues) !== FieldStatusFlags.None;
}

export function isNumericField(fieldType: FieldType): boolean {
    return fieldType === FieldType.Integer || fieldType === FieldType.Double;
}

export function isEmpty(value: any): boolean {
    return value === null || typeof value === "undefined" || (typeof value === "string" && value.trim().length === 0);
}

/**
 * Search for the given value in the expanded items list or in the global lists
 * @param store The workitem store to use for globals search
 * @param value The value to search for
 * @param expandedItems The list of expended items to search in
 * @param globals The list of global lists
 * @param caseInsensitive Perform case insensitive comparison if true
 */
export function inList(store: IWorkItemStore, value: string, expandedItems: string[], globals: string[], caseInsensitive?: boolean): boolean {
    let list: string[];

    if (expandedItems && contains(expandedItems, value, caseInsensitive)) {
        return true;
    }

    if (globals) {
        for (const setId of globals) {
            list = store.getConstantSet(setId);

            if (!list) {
                throw new Error(format("Constant set {0} is not ready. Operation cannot continue.", setId));
            }

            if (contains(list, value, caseInsensitive)) {
                return true;
            }
        }
    }

    return false;
}

export function intersect(a: string[], b: string[], caseInsensitive?: boolean): string[] {
    let hashtable: { [key: string]: string };
    let result: string[];

    if (!b) {
        return a;
    }

    if (b.length === 0) {
        return [];
    }

    if (a.length < b.length) {
        hashtable = fieldHashset(a, caseInsensitive);
    } else {
        hashtable = fieldHashset(b, caseInsensitive);
        b = a;
    }

    result = [];
    // TODO:Replat - do we need sort flags?
    // Utils_Array.copySortFlag(result, b);

    if (caseInsensitive) {
        for (const value of b) {
            if (hashtable.hasOwnProperty(value.toLocaleUpperCase())) {
                result[result.length] = value;
            }
        }
    } else {
        for (const value of b) {
            if (hashtable.hasOwnProperty(value)) {
                result[result.length] = value;
            }
        }
    }

    return result;
}

export function unionLists(store: IWorkItemStore, expandedItems: string[], globals: string[] | undefined): string[] {
    let result: string[];
    let list: string[];

    if (globals) {
        if (expandedItems) {
            result = expandedItems.slice(0);
            copySortFlag(result, expandedItems);
        } else {
            result = [];
            flagSorted(result, localeIgnoreCaseComparer);
        }

        for (const setId of globals) {
            list = store.getConstantSet(setId);

            if (!list) {
                throw new Error(format("Constant set {0} is not ready. Operation cannot continue.", setId));
            }

            result = union(result, list, true);
        }

        return result;
    } else {
        return expandedItems;
    }
}

export function contains(array: string[], value: string, caseInsensitive?: boolean): boolean {
    if (!caseInsensitive) {
        return array.includes(value);
    } else {
        const hashtable = fieldHashset(array, true);
        return hashtable.hasOwnProperty(value.toLocaleUpperCase());
    }
}

export function subtract(a: string[], b: string[], caseInsensitive?: boolean): string[] {
    let hashtable: { [key: string]: string };
    let result: string[];

    if (!b || b.length === 0) {
        return a;
    }

    result = [];
    // TODO:Replat - do we need sort flags?
    // Utils_Array.copySortFlag(result, a);

    hashtable = fieldHashset(b, caseInsensitive);

    if (caseInsensitive) {
        for (const value of a) {
            if (!hashtable.hasOwnProperty(value.toLocaleUpperCase())) {
                result[result.length] = value;
            }
        }
    } else {
        for (const value of a) {
            if (!hashtable.hasOwnProperty(value)) {
                result[result.length] = value;
            }
        }
    }

    return result;
}

export function union(a: string[], b: string[], caseInsensitive?: boolean): string[] {
    const comparer = caseInsensitive ? localeIgnoreCaseComparer : localeComparer;

    if (!b || b.length === 0) {
        return a;
    }

    if (!a || a.length === 0) {
        return b;
    }

    sortIfNotSorted(a, comparer);
    sortIfNotSorted(b, comparer);

    const result: string[] = [];
    flagSorted(result, comparer);

    let iA = 0;
    const lA = a.length;
    let iB = 0;
    const lB = b.length;
    let valueA: string;
    let valueB: string;
    let compareResult: number;

    while (iA < lA && iB < lB) {
        valueA = a[iA];
        valueB = b[iB];
        compareResult = comparer(valueA, valueB);

        if (compareResult === 0) {
            result[result.length] = valueA;
            iA++;
            iB++;
        } else if (compareResult > 0) {
            result[result.length] = valueB;
            iB++;
        } else {
            result[result.length] = valueA;
            iA++;
        }
    }

    // copy remaining
    while (iA < lA) {
        result[result.length] = a[iA];
        iA++;
    }

    while (iB < lB) {
        result[result.length] = b[iB];
        iB++;
    }

    return result;
}

export function isMatch(pattern: string, value: string): boolean {
    if (!pattern) {
        return true;
    }

    pattern = pattern.replace(/[.*+?|()\[\]\^\${}\\]/g, "\\$&");
    pattern = pattern.replace(/n/gi, "\\d").replace(/a/gi, "[a-zA-Z]").replace(/x/gi, "[a-zA-Z0-9]"); // order here is important
    pattern = "^" + pattern + "$";

    return new RegExp(pattern, "i").test(value);
}

export function isHistoricalRevision(field: IField): boolean {
    return field.workItem.isReadOnly();
}

export function isEditable(field: IField): boolean {
    if (field.workItem.isReadOnly() || isHistoricalRevision(field) || !FieldDefinitionUtils.isEditable(field.fieldDefinition)) {
        return false; // unconditionally readonly
    }

    let fieldId = field.fieldDefinition.id;

    switch (fieldId) {
        case CoreField.Rev:
        case CoreField.WorkItemType:
        case CoreField.CreatedBy:
        case CoreField.IsDeleted:
            return false;

        // Return the editability of the corresponding ID field
        case CoreField.AreaPath:
            fieldId = CoreField.AreaId;
            break;
        case CoreField.IterationPath:
            fieldId = CoreField.IterationId;
            break;

        default:
            return (field.status & FieldStatusFlags.ReadOnly) === FieldStatusFlags.None;
    }

    const newField = field.workItem.getFieldById(fieldId);

    return newField ? isEditable(newField) : false;
}

export function isReadOnly(field: IField): boolean {
    return !isEditable(field);
}

export function isRequired(field: IField): boolean {
    let fieldId = field.fieldDefinition.id;
    switch (fieldId) {
        case CoreField.IterationPath:
            fieldId = CoreField.IterationId;
            break;
        case CoreField.AreaPath:
            fieldId = CoreField.AreaId;
            break;
        default:
            return (field.status & FieldStatusFlags.Required) !== FieldStatusFlags.None;
    }

    return isRequired(field.workItem.getFieldById(fieldId) as IField);
}

export function canSortBy(field: IField): boolean {
    return (field.status & FieldFlags.Sortable) !== FieldStatusFlags.None;
}

export function isQueryable(field: IField): boolean {
    return (field.status & FieldFlags.Queryable) !== FieldStatusFlags.None;
}

export function convertFieldValueToDisplayString(value: any, parseOnlyIsoDates?: boolean): string {
    return convertFieldValueToInternal(value, FieldType.String, parseOnlyIsoDates).value as string;
}

export function getAllowedFieldValues(field: IField, workItem: IWorkItem): IObservableArray<string> {
    if (!field.allowedValues) {
        const result = computeAllowedFieldValues(field, workItem);

        field.allowedValues = new ObservableArray<string>(result);
    }

    return field.allowedValues;
}

export function computeAllowedFieldValues(field: IField, workItem: IWorkItem) {
    let result: string[] = [];
    const store = workItem.store;
    if (fieldHasList(field)) {
        const lists: IItemGlobalValueList = field.lists as IItemGlobalValueList;

        if (lists.allowedValues && lists.allowedValues.length > 0) {
            result = unionLists(store, lists.allowedValues[0].items, lists.allowedValues[0].globals);
            for (const allowedValues of lists.allowedValues) {
                result = intersect(result, unionLists(store, allowedValues.items, allowedValues.globals), true);
            }
        }

        if (lists.suggestedValues && lists.suggestedValues.length > 0) {
            let allSuggested: string[] = [];
            for (const suggestedValues of lists.suggestedValues) {
                allSuggested = union(allSuggested, unionLists(store, suggestedValues.items, suggestedValues.globals), true);
            }

            if (result.length) {
                result = intersect(result, allSuggested);
            } else {
                result = allSuggested;
            }
        }

        if (lists.allowExistingValue) {
            const originalValue = workItem.getFieldValueById(field.fieldDefinition.id, true);

            const isDateParsingFixEnabled = !isFeatureFlagEnabled(workItem.getPageContext(), FeatureFlags.DateParsingFixDisabled, false)

            if (!isEmpty(originalValue)) {
                if (result.length) {
                    result = union(result, [convertFieldValueToDisplayString(originalValue, isDateParsingFixEnabled)], true);
                } else {
                    result = [convertFieldValueToDisplayString(originalValue, isDateParsingFixEnabled)];
                }
            }
        }

        if (result.length && lists.prohibitedValues && lists.prohibitedValues.length > 0) {
            for (const prohibitedValues of lists.prohibitedValues) {
                result = subtract(result, unionLists(store, prohibitedValues.items, prohibitedValues.globals), true);
            }
        }
    }

    if (isNumericField(field.fieldDefinition.type)) {
        // TODO:Replat - do we need sort flag stuff?
        sortIfNotSorted(result as unknown as number[], (a: number, b: number) => {
            return a - b;
        });
    } else if (
        field.fieldDefinition.id === CoreField.State &&
        field.fieldDefinition.workItemType &&
        field.fieldDefinition.workItemType.orderedStates &&
        field.fieldDefinition.workItemType.orderedStates.length > 0
    ) {
        result = field.fieldDefinition.workItemType.orderedStates.filter((state: string) => (result as string[]).indexOf(state) !== -1);
    } else {
        // TODO:Replat - do we need sort flag stuff?
        sortIfNotSorted(result, localeComparer);
    }

    return result;
}

export function setAllowedFieldValues(field: IField, values: string[]) {
    if (fieldHasList(field) && !isIdentityPickerSupportedForField(field)) {
        if (field.allowedValues) {
            field.allowedValues.value = values;
        } else {
            field.allowedValues = new ObservableArray<string>(values);
        }
    }
}

export function isIdentityPickerSupportedForField(field: IField) {
    return field && field.fieldDefinition && field.fieldDefinition.isIdentity;
}

export function isFieldValid(field: IField): boolean {
    return (field.status & FieldStatusFlags.InvalidMask) === FieldStatusFlags.None;
}

export function validateIdentityFieldValue(field: IField, workItem: IWorkItem): void {
    const validatedValue = workItem.getFieldValueById(field.fieldDefinition.id);
    const validationKey = new FieldValidationKey(field.fieldDefinition.id, validatedValue, field.filterByScope!);
    if (isIdentityPickerSupportedForField(field) && isFieldValid(field) && !(validatedValue instanceof ServerDefaultValue)) {
        // we dont want to run ruleengine on the field if its value is set by a serverdefaultvalue rule, as the rule engine API doesnt understand ServerDefault Rule
        workItem.beginValidate(
            field.fieldDefinition.id,
            validationKey,
            () => {
                // if the request comes back, but the current value or scope is changed for the field compared with those when request sent,
                // we just ignore the result
                // the response for the perticular scope and value will handle, since every value change or scope change will trigger eager validation
                if (
                    new FieldValidationKey(
                        field.fieldDefinition.id,
                        workItem.getFieldValueById(field.fieldDefinition.id),
                        field.filterByScope!
                    ).equals(validationKey)
                ) {
                    field.error = "";
                }
            },
            (error: any, referenceName?: string) => {
                let validatedField: IField = field;
                if (referenceName) {
                    validatedField = workItem.getFieldValueByName(referenceName);
                }

                // If when we get the validation failure the field is already
                // invalid then just let it be
                if (
                    isFieldValid(validatedField) &&
                    new FieldValidationKey(
                        field.fieldDefinition.id,
                        workItem.getFieldValueById(field.fieldDefinition.id),
                        field.filterByScope!
                    ).equals(validationKey)
                ) {
                    validatedField.error = error.message;
                    validatedField.status = FieldStatusFlags.InvalidIdentityField;
                    // TODO:Replat
                    // workItem.fireFieldChange([validatedField.fieldDefinition.id]);
                }
            }
        );
    }
}

export function htmlDecode(value: string): string {
    const doc = new DOMParser().parseFromString(value, "text/html");
    return doc.documentElement.innerText ?? value;
}
